import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnInit,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { HttpClient } from '@angular/common/http';
import { FilterConfig, GridConfig } from '../../shared/dynamic-grid/grid-model/grid-config.model';
import { Toast, ToastComponent } from '../../shared/toast/toast.component';
import { API_ENDPOINTS } from '../../shared/constants/apiEnpoints';
import { debounceTime, finalize, firstValueFrom, Subject, takeUntil, Subscription } from 'rxjs';
import { ACKNOWLEDGEABLE_ERROR_STATUS_CODES, ACKNOWLEDGEABLE_SUCCESS_STATUS_CODES, MAX_INT_32 } from '../../shared/constants/values';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import { GridDataService } from '../../shared/dynamic-grid/grid-service/grid-data.service';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import moment from 'moment';
import { BREADCRUMB_NAMES } from '../../shared/constants/breadCrumbPaths';
import { marked } from 'marked';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { ColumnConfig, computeFitSettings, ExportPdfOptions, formatCellByType, timestamp } from '../../shared/export-to-pdf/pdfHelpers';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { ColumnPreferencesService } from '../../shared/column-preference/columnpreference.service';

const vfs = (pdfFonts as any).pdfMake?.vfs ?? (pdfFonts as any).vfs;
(pdfMake as any).vfs = vfs;

export interface ColumnPreference {
  PreferenceKeyName: string;
  HiddenColumns: Set<string>;
}

@Component({
  selector: 'app-audit-report',
  templateUrl: './audit-report.component.html',
  styleUrl: './audit-report.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditReportComponent implements OnInit, OnDestroy {
  onCheckboxChange($event: Event, arg1: string) {
    throw new Error('Method not implemented.');
  }
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(ToastComponent) toastComponent!: ToastComponent;

  @Input() gridConfig!: GridConfig;
  @Input() getDataURL!: string;
  @Input() getUserEmailURL!: string;
  @Input() postUrl!: string;
  @Input() deleteUrl!: string;
  @Input() toggleUrl!: string;
  @Input() customIsErrorLog: (element: any) => boolean;
  @Input() sortBy: string = '';
  @Input() sortOrder: string = 'desc';
  @Input() onAdd!: Function;
  @Input() onEdit!: Function;

  @Output() addClicked = new EventEmitter<boolean>();
  @Output() editClicked = new EventEmitter<any>();
  @Output() onToggleShowToastEmitter: EventEmitter<Toast> = new EventEmitter<Toast>();
  @Output() onRowClick: EventEmitter<any> = new EventEmitter<any>();

  dataSource = new MatTableDataSource();
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  private subscriptions: Subscription[] = [];
  minFromDate: string = '';
  maxToDate: string = '';
  displayedColumns!: string[];
  isDropdownOpen: boolean = false;
  showDateFilterDropdown = false;
  hasFilters: boolean;
  isExportMenuOpen: boolean;
  isLoading: boolean = true;
  pageNumber: number = 1;
  pageSize: number = 50;
  searchInputValue: string = '';
  searchQuery: string = '';
  showErrorCard: boolean = false;
  errorTitle: string = '';
  errorMessage: string = '';
  totalItems: number;
  isServerSidePagination: boolean = true;
  pageChanged: boolean = true;
  pageSizeOptions: number[] = [50, 100, 150, 200, MAX_INT_32];
  refreshTimeout: any;
  emailOptions: any[] = [];
  filters: { [key: string]: any } = {};
  openDropdownKey: any = null;

  isFilterDropdownOpen: { [key: string]: boolean } = {};
  customDateRange = {
    from: '',
    to: '',
  };

  isDateInputDisabled: boolean = false;
  userRole = localStorage.getItem('userRole');
  userEmail = localStorage.getItem('userEmail');
  filtered: { [key: string]: any } = {};
  appliedFilter: boolean = false;
  resetAll: boolean = false;
  uniqueTableName: string;
  hideAuditColumns: boolean = JSON.parse(localStorage.getItem('hideAuditColumns')) || false;
  emptyDataMessage: string = 'No data found at this moment';
  suppressAllChange: boolean = false;
  private _totalRecordsCount: number = 0;

  isColumnSelectorOpen = false;
  isSavingPreferences = false;
  visibleColumns: Set<string> = new Set();
  private userId: string | null = null;
  columnPreference: ColumnPreference;

  constructor(
    private http: HttpClient,
    private gridDataService: GridDataService,
    public dialog: MatDialog,
    private _router: Router,
    private elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef,
    private datePipe: DatePipe,
    private columnPrefService: ColumnPreferencesService,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth
  ) { }

  getGridColumnNames() {
    return this.gridConfig.columns
      .filter(col => this.visibleColumns.has(col.jsonKey))
      .map((e) => e.jsonKey);
  }

  async ngOnInit(): Promise<void> {
    this.gridConfig = {
      columns: [
        { label: 'Table Name', jsonKey: 'tableName', dataType: 'string', isSortable: true },
        { label: 'Record', jsonKey: 'parentKey', dataType: 'string', isSortable: false },
        { label: 'Action', jsonKey: 'actionName', dataType: 'string', isSortable: true },
        { label: 'Column Name', jsonKey: 'columnName', dataType: 'string', isSortable: true },
        { label: 'Old Value', jsonKey: 'oldValue', dataType: 'string', isSortable: true },
        { label: 'New Value', jsonKey: 'newValue', dataType: 'string', isSortable: true },
        { label: 'Created By / Modified By', jsonKey: 'modifiedByUserEmail', dataType: 'string', isSortable: true },
        { label: 'Created At / Modified At', jsonKey: 'modifiedAt', dataType: 'date', width: 'full', isSortable: true }
      ],
      filters: [
        {
          label: 'Scope', jsonKey: 'auditReportName', options: {
            data: [{ id: 'All', name: 'All', checked: true },
            { id: 'PMSMaster', name: 'PMS', checked: true },
            { id: 'Practice', name: 'Practices', checked: true },
            { id: 'PMSObject', name: 'PMS Objects', checked: true },
            { id: 'PMSObjectWisePHI', name: 'PHI Records', checked: true },
            { id: 'PMSSchemaIntegrity', name: 'PMS Schema Integrity', checked: true },
            { id: 'LoadSchedule', name: 'Load Schedules', checked: true },
            { id: 'PracticeLoadConfiguration', name: 'Practice Load Configurations', checked: true },
            { id: 'User', name: 'User', checked: true }
            ]
          }, type: 'multi'
        },
        { label: 'Users', jsonKey: 'userEmail', options: [], type: 'multi' },
        { label: 'Log Dates', jsonKey: 'logDates', options: [], type: 'date' },
      ],
      editable: false,
      deletable: false,
      exportable: true,
      uniqueTableName: 'AuditReport'
    };

    // Set default filters
    this.filters = {
      auditReportName: [
        { id: 'All', name: 'All', checked: true },
        { id: 'PMSMaster', name: 'PMS', checked: true },
        { id: 'Practice', name: 'Practices', checked: true },
        { id: 'PMSObject', name: 'PMS Objects', checked: true },
        { id: 'PMSObjectWisePHI', name: 'PHI Records', checked: true },
        { id: 'LoadSchedule', name: 'Load Schedules', checked: true },
        { id: 'PracticeLoadConfiguration', name: 'Practice Load Configurations', checked: true },
        { id: 'User', name: 'User', checked: true }
      ],
      userEmail: [],
      sortOrder: 'desc'
    };

    this.getDataURL = API_ENDPOINTS.AUDIT_REPORT;
    this.getUserEmailURL = API_ENDPOINTS.USERS_ROLE;
    this.hasFilters = this.gridConfig.filters == undefined ? false : true;
    this.uniqueTableName = this.gridConfig.uniqueTableName;
    this.isLoading = true;

    setTimeout(() => {
      this.columnPreference = this.getPreferenceKeyName();
      this.loadColumnPreferences(this.columnPreference.PreferenceKeyName);
    }, 0);

    await this.fetchUserEmailOptions().then(async () => {
      this.isLoading = true;
      await this.fetchData();
      this.isLoading = false;
    });

    this.searchSubject.pipe(
      debounceTime(500),
      takeUntil(this.destroy$)
    ).subscribe((searchQuery) => {
      this.searchQuery = searchQuery;
      this.pageNumber = 1;
      this.fetchData();
    });

    this.setupColumns();
  }

  ngAfterViewInit() {
    this.sortBy = 'modifiedAt';
    this.paginator.pageSize = this.pageSize;
    this.sort.active = this.sortBy;
    this.sort.direction = this.sortOrder as SortDirection;
    this.sort.start = this.sortOrder as SortDirection;
    this.dataSource.sort = this.sort;
    this.sort.sortChange.emit();
    this.changeDetectorRef.detectChanges();
    this.paginator.page.subscribe((event: PageEvent) => {
      this.paginator.length = this.totalItems;
      this.pageNumber = event.pageIndex + 1;
      this.pageSize = event.pageSize;
      this.fetchData();
    });
    this.sort.sortChange.subscribe(() => {
      this.sortBy = this.sort.active;
      this.sortOrder = this.sort.direction;
      this.pageNumber = 1;
      this.fetchData();
      this.paginator.length = this.totalItems;
    });
  }

  async fetchData(): Promise<void> {
    this.isLoading = true;
    try {
      const localFilters = JSON.parse(JSON.stringify(this.filters));
      localFilters.sortOrder = this.sortOrder;

      if (localFilters['auditReportName']) {
        localFilters['auditReportName'] = localFilters['auditReportName'].filter(
          (opt: any) => opt.id !== 'All'
        );
      }

      if (localFilters['userEmail']) {
        localFilters['userEmail'] = localFilters['userEmail'].filter(
          (opt: any) => opt.id !== 'All'
        );
      }

      let response = await this.gridDataService
        .fetchData(
          this.getDataURL,
          this.pageNumber,
          this.pageSize,
          this.searchQuery,
          this.sortBy,
          this.sortOrder,
          localFilters
        )
        .toPromise();

      if (response.isSuccess === false) {
        this.handleError({
          status: response.statusCode,
          error: {
            errorMessage: response.errorMessage,
            detailedErrorMessage: response.detailedErrorMessage,
          },
        });
        return;
      }

      let rawData = response.data.historyData;

      if (response.data.historyData !== null && response.data.historyData.length > 0) {
        const parentKeyMap = new Map<string, number>();
        let currentGroupIndex = 0;

        for (let row of rawData) {
          const key = row.parentKey;
          if (!parentKeyMap.has(key)) {
            currentGroupIndex++;
            parentKeyMap.set(key, currentGroupIndex);
          }
          (row as any).groupIndex = parentKeyMap.get(key);
        }

        const logDates = response?.data?.logDates;
        const minDate = new Date(Date.parse(logDates.startDate + 'Z'));
        const maxDate = new Date(Date.parse(logDates.endDate + 'Z'));

        // Log as plain date strings
        this.customDateRange = {
          from: this.extractDateFromISOString(minDate.toISOString()),
          to: this.extractDateFromISOString(maxDate.toISOString())
        };

        this.showDateFilterDropdown = true;
        this.minFromDate = this.customDateRange.from;
        this.maxToDate = this.customDateRange.to;
        this.getDateFilterLabel();
        this.isDateInputDisabled = false;
      } else {
        this.showDateFilterDropdown = false;
        this.isDateInputDisabled = true;
      }

      this._totalRecordsCount = response.totalRecordsCount;
      this.totalItems = response.totalRecordsCount;
      this.paginator.length = this.totalItems;
      this.dataSource.data = rawData;
      this.dataSource.sort = null;
      // Check if data is empty
      if (response.data.historyData === null || response.data.historyData.length === 0) {
        this.emptyDataMessage = 'There are no items to display at the moment.';
      } else {
        this.emptyDataMessage = '';
      }

      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    } catch (error) {
      this.handleError(error);
    }
  }

  async fetchUserEmailOptions(): Promise<void> {
    const currentUserEmail = localStorage.getItem('userEmail')?.trim();

    const response = await firstValueFrom(
      this.http.get<any>(this.getUserEmailURL, { params: { pageSize: MAX_INT_32 } })
    );

    let emailList = response.data.map(option => ({
      ...option,
      name: option.email === currentUserEmail ? `${option.email} (Me)` : option.email,
      id: option.email
    })).sort((a, b) => a.name.localeCompare(b.name));

    emailList = emailList.filter(option => option.id !== 'pms_admin@dentalcorp.ca');
    emailList.unshift({
      name: 'pms_admin@dentalcorp.ca (System User)',
      id: 'pms_admin@dentalcorp.ca',
      checked: true
    });
    emailList.unshift({
      name: 'All',
      id: 'All',
      checked: true
    });

    emailList = emailList.map(opt => ({ ...opt, checked: true }));

    response.data = emailList;
    this.gridConfig.filters.find(filter => filter.jsonKey === 'userEmail').options = response;
    this.filters["userEmail"] = emailList;
    this.emailOptions = this.filters["userEmail"];
  }

  shouldHideButton(filterConfig: FilterConfig): boolean {
    return filterConfig.type === 'date' && !this.showDateFilterDropdown;
  }

  extractDateFromISOString(dateTimeStr: string): string {
    if (!dateTimeStr) return '';
    return new Date(dateTimeStr).toISOString().split('T')[0];
  }

  isFilterOptionSelected(filterConfig: FilterConfig, option: any): boolean {
    // First check if the option is explicitly selected in filters
    const selected = this.filters[filterConfig.jsonKey]?.some(
      (selectedOption: any) => selectedOption.id === option.id
    );

    // If not explicitly selected, fallback to check if it's "checked: true" by default in config
    if (!selected) {
      return filterConfig.options?.data?.some(
        (opt: any) => opt.id === option.id && opt.checked === true
      );
    }

    return true;
  }

  getSelectedOptionNames(filterConfig: FilterConfig): string {
    if (this.isAllOptionsSelected(filterConfig)) {
      return 'All';
    }
    const selectedOptions = filterConfig.options?.data?.filter((option: any) =>
      this.filters[filterConfig.jsonKey]?.some(
        (selectedOption: any) => selectedOption.id === option.id
      )
    );
    const selectedOptionNames = selectedOptions?.map((option: any) => option.name) || [];

    if (selectedOptionNames.length === this.gridConfig.filters.find(f => f.jsonKey === filterConfig.jsonKey).options.data.length - 1) {
      selectedOptionNames.unshift('All');
    }

    return selectedOptionNames.length > 1
      ? `${selectedOptionNames[0]}...`
      : selectedOptionNames[0] || '';
  }

  showToast(toast: Toast) {
    this.toastComponent.showToast(toast);
  }

  onFilterOptionClick(filter: FilterConfig, option: any): void {
    if (filter.type === 'single') {
      this.filters[filter.jsonKey] = option.id;
    } else if (filter.type === 'multi') {
      if (!this.filters[filter.jsonKey]) {
        this.filters[filter.jsonKey] = [];
      }
      const index = this.filters[filter.jsonKey].indexOf(option);
      if (index === -1) {
        this.filters[filter.jsonKey].push(option);
      } else {
        this.filters[filter.jsonKey].splice(index, 1);
      }
    }
    this.pageNumber = 1;
  }

  onFilterOptionChange(
    filterConfig: FilterConfig,
    option: any,
    checked: boolean
  ): void {

    const jsonKey = filterConfig.jsonKey;
    option.checked = checked;

    // Initialize if needed
    if (!this.filters[jsonKey]) {
      this.filters[jsonKey] = [];
    }

    // Handle "All" checkbox
    if (option.id === 'All') {

      if (this.suppressAllChange) {
        return;
      }

      if (checked) {
        // Select all options (excluding "All" itself)
        const allOptions = filterConfig.options.data;//.filter(o => o.id !== 'All');
        this.filters[jsonKey] = Array.from(
          new Map([...allOptions].map(o => [o.id, o])).values()
        );

      } else {
        // Deselect all 
        filterConfig.options.data.forEach(o => (o.checked = false));
        this.filters[jsonKey] = [];
      }
      return;
    } else { // Non-"All" checkbox

      const allItem = filterConfig.options.data.find(o => o.id === 'All');

      if (!checked) {
        // Any deselection => uncheck "All"
        if (allItem) {
          this.suppressAllChange = true;
          allItem.checked = false;
          Promise.resolve().then(() => (this.suppressAllChange = false));
        }
      } else {
        // If all non-All items are checked => check "All"
        const allNonAllChecked = filterConfig.options.data
          .filter(o => o.id !== 'All')
          .every(o => !!o.checked);

        if (allItem) {
          this.suppressAllChange = true;
          allItem.checked = allNonAllChecked;
          Promise.resolve().then(() => (this.suppressAllChange = false));
        }
      }
    }

    const selectedNonAll = filterConfig.options.data
      .filter(o => o.id !== 'All' && o.checked === true)
      .map(o => ({ id: o.id, name: o.name })); // clone

    this.filters[jsonKey] = selectedNonAll;

    this.filters[jsonKey] = selectedNonAll.map(o => ({ id: o.id, name: o.name }));

    if (this.filters[jsonKey].length === 0) {
      delete this.filters[jsonKey];
    }
  }

  onCustomDateChange(filterConfig: FilterConfig): void {
    const from = new Date(this.customDateRange.from);
    const to = new Date(this.customDateRange.to);

    // Optional: prevent future dates or invalid ranges
    if (from && to && from <= to) {
      this.filters[filterConfig.jsonKey] = {
        startDate: from.toISOString(),
        endDate: to.toISOString(),
      };
    }
  }

  private configureMarked(): void {
    const renderer = new marked.Renderer();
    renderer.link = ({ href, title, text }) => {
      const titleAttr = title ? ` title="${title}"` : '';
      return `<a href="${href}" target="_blank" rel="noopener noreferrer"${titleAttr}>${text}</a>`;
    };
    marked.setOptions({ renderer });
  }

  setupColumns(): void {
    this.displayedColumns =
      this.gridConfig.editable || this.gridConfig.deletable
        ? ['action', ...this.gridConfig.columns.map((c) => c.jsonKey)]
        : this.gridConfig.columns.map((c) => c.jsonKey);
  }

  disableSuccessAcknowldgeToggle(element: any): boolean {
    if (element.pmsStatusCode) {
      return ACKNOWLEDGEABLE_SUCCESS_STATUS_CODES.includes(
        element.pmsStatusCode
      )
        ? true
        : false;
    }
    return false;
  }

  onToggleChange(element: any, jsonKey: string, toggleValue: boolean): void {
    const toggleUrl = this.gridConfig.columns.find(
      (column) => column.jsonKey === jsonKey
    )?.toggleUrl;

    const patchUrl = toggleUrl
      ? toggleUrl.replace(':id', element.id.toString())
      : this.toggleUrl;

    this.gridDataService
      .toggleData(patchUrl, element.id, jsonKey, toggleValue)
      .subscribe(
        (response) => {
          element[jsonKey] = response[jsonKey];
          const index = this.dataSource.data.findIndex(
            (item: any) => item.id === element.id
          );
          if (index !== -1) {
            this.dataSource.data[index][jsonKey] = response[jsonKey];
          }
          this.onToggleShowToastEmitter.emit({
            severity: 'success',
            title: 'Success',
            message: response?.data,
          });
        },
        (error) => {
          console.error('Error updating toggle value:', error);
          this.onToggleShowToastEmitter.emit({
            severity: 'error',
            title: 'Error',
            message: error?.error?.errorMessage,
          });
        }
      );
  }

  formatDateTime(date: string | Date) {
    if (date == null || date == '')
      return '';
    const userTimeZone = moment.tz.guess();
    const userDateTime = moment.utc(date).tz(userTimeZone);
    return userDateTime.format('YYYY-MM-DD HH:mm');
  }

  getMakrDownText(isMarkDownSupported: boolean, text: string) {
    return isMarkDownSupported ? marked(text) : '';
  }

  formatNumber(value: number | null): string | number {
    return value ? new Intl.NumberFormat('en-US').format(value) : value;
  }

  rowClick(data, event) {
    this.onRowClick.emit(data);
  }

  toggleFilterDropdown(filterKey: string, event: Event): void {
    for (let key in this.isFilterDropdownOpen) {
      if (filterKey !== key) {
        this.isFilterDropdownOpen[key] = false;
      }
    }
    this.isFilterDropdownOpen[filterKey] =
      !this.isFilterDropdownOpen[filterKey];
  }

  closeAllDropdowns(key: string) {
    this.isColumnSelectorOpen = false;
    this.isDropdownOpen = false;
    this.changeDetectorRef.markForCheck();
    this.isFilterDropdownOpen[key] = false;
    this.changeDetectorRef.detectChanges();
  }

  onDateChange(filterConfig: FilterConfig, selectedDate: string): void {
    this.filters[filterConfig.jsonKey] = selectedDate;
  }

  isFilterApplied(filterConfig: FilterConfig): boolean {
    return (
      this.filters[filterConfig.jsonKey] &&
      this.filters[filterConfig.jsonKey].length > 0
    );
  }

  getDateFilterLabel(): string {
    if (!this.customDateRange.from || !this.customDateRange.to) {
      return '';
    }

    const from = new Date(this.customDateRange.from);
    const to = new Date(this.customDateRange.to);

    const diffDays = Math.ceil(
      (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)
    ) + 1;

    return `Last ${diffDays} day${diffDays > 1 ? 's' : ''}`;
  }

  hasFilterOptionsSelected(filterConfig: FilterConfig): boolean {
    if (filterConfig.type === 'single') {
      return !!this.filters[filterConfig.jsonKey];
    } else if (filterConfig.type === 'multi') {
      return !!this.filters[filterConfig.jsonKey]?.length;
    } else if (filterConfig.type === 'date') {
      return !!this.filters[filterConfig.jsonKey];
    }
    return false;
  }

  getAppliedFilterCount(filterConfig: FilterConfig): number {
    this.filters[filterConfig.jsonKey]
    if (this.filters[filterConfig.jsonKey].length === this.gridConfig.filters.find(f => f.jsonKey === filterConfig.jsonKey).options.data.length - 1)
      return this.filters[filterConfig.jsonKey].length + 1;

    if (filterConfig.type === 'single') {
      return this.filters[filterConfig.jsonKey] ? 1 : 0;
    } else if (filterConfig.type === 'multi') {
      return this.filters[filterConfig.jsonKey]?.length || 0;
    } else if (filterConfig.type === 'date') {
      return this.filters[filterConfig.jsonKey] ? 1 : 0;
    }
    return 0;
  }

  getDateFilterOptions(
    filterConfig: FilterConfig
  ): { label: string; value: string }[] {
    return [
      { label: 'Custom', value: 'custom' },
    ];
  }

  resetFilter() {
    this.filters = {
      auditReportName: [
        { id: 'All', name: 'All', checked: true },
        { id: 'PMSMaster', name: 'PMS', checked: true },
        { id: 'Practice', name: 'Practices', checked: true },
        { id: 'PMSObject', name: 'PMS Objects', checked: true },
        { id: 'PMSObjectWisePHI', name: 'PHI Records', checked: true },
        { id: 'LoadSchedule', name: 'Load Schedules', checked: true },
        { id: 'PracticeLoadConfiguration', name: 'Practice Load Configurations', checked: true },
        { id: 'User', name: 'User', checked: true }
      ],
      userEmail: this.emailOptions,
      sortOrder: 'desc'
    };

    this.customDateRange = {
      from: '',
      to: ''
    };

    this.appliedFilter = false;
    this.resetAll = true;
    this.searchQuery = '';
    this.searchInputValue = '';
    this.paginator.firstPage();
    localStorage.removeItem('choosenFilters');
    this.fetchData();
    this.closeFilterDropdowns();
  }

  async exportData(): Promise<void> {
    const data = this.dataSource.data;

    if (data === undefined || data === null || data.length === 0) {
      this.isColumnSelectorOpen = false;
      this.isSavingPreferences = false;
      this.isDropdownOpen = false;
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
      this.toastComponent?.showToast({
        severity: 'warn',
        title: 'Warning',
        message: 'No data available to export.',
        duration: 3000
      });
      this.changeDetectorRef.detectChanges();
      return;
    }

    // Clone columns config
    const columns = this.gridConfig.columns
      .filter(col => this.visibleColumns.has(col.jsonKey))
      .map(column => ({ ...column }));
    const headers = columns.map(column => column.label.toUpperCase());

    // Convert data to JSON with transformations
    const jsonData = data.map((row: any) => {
      const rowData: any = {};
      columns.forEach((column) => {
        const value = row[column.jsonKey];

        if (typeof value === 'boolean') {
          rowData[column.label.toUpperCase()] = value ? 'Yes' : 'No';

        } else if (column.dataType === 'date') {
          if (value) {
            const formattedDate = moment
              .utc(value, 'YYYY-MM-DD HH:mm:ss')
              .local()
              .format('YYYY-MM-DD HH:mm:ss');
            rowData[column.label.toUpperCase()] = formattedDate;
          } else {
            rowData[column.label.toUpperCase()] = '';
          }

        } else {
          rowData[column.label.toUpperCase()] = value ?? '';
        }
      });

      return rowData;
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    worksheet.addRow(headers);
    jsonData.forEach(row => {
      const rowData = headers.map(header => row[header]);
      worksheet.addRow(rowData);
    });

    columns.forEach((_, index) => {
      worksheet.getColumn(index + 1).width = 20;
    });

    const currentURLSegements = this._router.url.split('/');
    const currentPath = currentURLSegements[currentURLSegements.length - 1];
    const fileName = this.generateFilename(
      BREADCRUMB_NAMES[currentPath],
      'xlsx'
    );

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, fileName);

    this.isDropdownOpen = false;
  }

  generateFilename(name: string, format: string) {
    const today = moment();
    name = name.replace(' ', '_');
    // corner case for assign and configure release
    name =
      name.includes('Assign') || name.includes('Configure')
        ? name + '_Release'
        : name;
    const dateString = today.format('YYYY-MM-DD-HH-mm-ss');
    // Concatenate the date with the name
    return `${name}_${dateString}.${format}`;
  }

  setAuditColumnToggleState() {
    if (this.hideAuditColumns) {
      this.displayedColumns =
        this.gridConfig.editable || this.gridConfig.deletable
          ? [
            'action',
            ...this.gridConfig.columns
              .filter((e) => !e.hideOnToggle)
              .map((c) => c.jsonKey),
          ]
          : this.gridConfig.columns
            .filter((e) => !e.hideOnToggle)
            .map((c) => c.jsonKey);
    } else {
      this.displayedColumns =
        this.gridConfig.editable || this.gridConfig.deletable
          ? ['action', ...this.gridConfig.columns.map((c) => c.jsonKey)]
          : this.gridConfig.columns.map((c) => c.jsonKey);
    }
    localStorage.setItem('hideAuditColumns', this.hideAuditColumns.toString());
  }

  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  async exportAllData(): Promise<void> {
    this.isLoading = true;

    try {
      const totalRecords = this._totalRecordsCount;

      // Build filter object
      const reportFilters: any = {
        auditReportName: [],
        userEmail: [],
        sortOrder: 'desc'
      };

      const allReportNames = this.gridConfig.filters
        .find(f => f.jsonKey === 'auditReportName')
        ?.options.data.filter(o => o.id !== 'All');

      reportFilters.auditReportName = allReportNames ?? [];

      const allUserEmail = this.gridConfig.filters
        .find(f => f.jsonKey === 'userEmail')
        ?.options.data.filter(o => o.id !== 'All');

      reportFilters.userEmail = allUserEmail ?? [];

      // API Call - fetch all rows
      const response = await this.gridDataService
        .fetchData(
          this.getDataURL,
          1,
          -1,
          '',
          this.sortBy,
          this.sortOrder,
          reportFilters
        )
        .toPromise();

      if (response.isSuccess === false) {
        this.handleError({
          status: response.statusCode,
          error: {
            errorMessage: response.errorMessage,
            detailedErrorMessage: response.detailedErrorMessage,
          },
        });
        return;
      }

      const allData = response.data.historyData;

      if (allData === undefined || allData === null || allData.length === 0) {
        this.isColumnSelectorOpen = false;
        this.isSavingPreferences = false;
        this.isDropdownOpen = false;
        this.isLoading = false;
        this.changeDetectorRef.detectChanges();
        this.toastComponent?.showToast({
          severity: 'warn',
          title: 'Warning',
          message: 'No data available to export.',
          duration: 3000
        });
        this.changeDetectorRef.detectChanges();
        return;
      }

      // Prepare column headers
      const columns = this.gridConfig.columns
        .filter(col => this.visibleColumns.has(col.jsonKey))
        .map(column => ({ ...column }));
      const headers = columns.map(column => column.label.toUpperCase());

      // Prepare row data
      const jsonData = allData.map((row: any) => {
        const rowData: any = {};

        columns.forEach((column) => {
          const value = row[column.jsonKey];

          if (typeof value === 'boolean') {
            rowData[column.label.toUpperCase()] = value ? 'Yes' : 'No';

          } else if (column.dataType === 'date') {
            if (value) {
              const formattedDate = moment
                .utc(value, 'YYYY-MM-DD HH:mm:ss')
                .local()
                .format('YYYY-MM-DD HH:mm:ss');
              rowData[column.label.toUpperCase()] = formattedDate;
            } else {
              rowData[column.label.toUpperCase()] = '';
            }

          } else {
            rowData[column.label.toUpperCase()] = value ?? '';
          }
        });

        return rowData;
      });

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Sheet1');

      worksheet.addRow(headers);
      jsonData.forEach(row => {
        const rowData = headers.map(header => row[header]);
        worksheet.addRow(rowData);
      });

      columns.forEach((_, index) => {
        worksheet.getColumn(index + 1).width = 20;
      });

      const currentURLSegements = this._router.url.split('/');
      const currentPath = currentURLSegements[currentURLSegements.length - 1];
      const fileName = this.generateFilename(
        'All_' + BREADCRUMB_NAMES[currentPath],
        'xlsx'
      );

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, fileName);

      this.isDropdownOpen = false;
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    } catch (error) {
      this.isDropdownOpen = false;
      this.isLoading = false;
      this.handleError(error);
    }
  }

  // Export data to PDF
  async exportDataToPDF(reportTitle: string, reportType: string): Promise<void> {
    let data: any[] = [];
    switch (reportType) {
      case 'Current':
        data = this.dataSource.data;
        break;
      case 'All':
        this.isLoading = true;
        try {
          // Build filter object
          const reportFilters: any = {
            auditReportName: [],
            userEmail: [],
            sortOrder: 'desc'
          };

          const allReportNames = this.gridConfig.filters
            .find(f => f.jsonKey === 'auditReportName')
            ?.options.data.filter(o => o.id !== 'All');

          reportFilters.auditReportName = allReportNames ?? [];

          const allUserEmail = this.gridConfig.filters
            .find(f => f.jsonKey === 'userEmail')
            ?.options.data.filter(o => o.id !== 'All');

          reportFilters.userEmail = allUserEmail ?? [];

          // API Call - fetch all rows
          const response = await this.gridDataService
            .fetchData(
              this.getDataURL,
              1,
              -1,
              '',
              this.sortBy,
              this.sortOrder,
              reportFilters
            )
            .toPromise();

          if (response.isSuccess === false) {
            this.isColumnSelectorOpen = false;
            this.isSavingPreferences = false;
            this.isDropdownOpen = false;
            this.isLoading = false;
            this.changeDetectorRef.detectChanges();
            this.toastComponent?.showToast({
              severity: 'warn',
              title: 'Warning',
              message: 'No data available to export.',
              duration: 3000
            });
            this.changeDetectorRef.detectChanges();
            return;
          }

          // Set data
          data = response.data.historyData;
        } catch (error) {
          this.isDropdownOpen = false;
          this.isLoading = false;
          this.changeDetectorRef.detectChanges();
          this.handleError(error);
        }
        break;
      default:
        data = this.dataSource.data;
        break;
    }

    if (data === undefined || data === null || data.length === 0) {
      this.isDropdownOpen = false;
      this.isColumnSelectorOpen = false;
      this.isSavingPreferences = false;
      this.isDropdownOpen = false;
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
      this.toastComponent?.showToast({
        severity: 'warn',
        title: 'Warning',
        message: 'No data available to export.',
        duration: 3000
      });
      this.changeDetectorRef.detectChanges();
      return;
    }

    data.forEach((row: any) => {
      this.gridConfig.columns.forEach((column) => {
        if (!this.visibleColumns.has(column.jsonKey)) {
          return; // Skip hidden columns
        }
        const value = row[column.jsonKey];

        if (typeof value === 'boolean') {
          // Convert boolean to Yes/No
          row[column.jsonKey] = value ? 'Yes' : 'No';

        } else if (column.dataType === 'date' || column.dataType === 'dateOnly') {
          // Format date if present
          if (value) {
            const formattedDate = moment
              .utc(value, 'YYYY-MM-DD HH:mm:ss') // Use correct tokens
              .local()
              .format('YYYY-MM-DD HH:mm:ss');
            row[column.jsonKey] = formattedDate;
          } else {
            row[column.jsonKey] = '';
          }

        } else {
          // Default: keep value or empty string
          row[column.jsonKey] = value ?? '';
        }
      });
    });

    this.isLoading = false;
    this.isDropdownOpen = false;
    this.changeDetectorRef.detectChanges();

    const currentURLSegements = this._router.url.split('/');
    const currentPath = currentURLSegements[currentURLSegements.length - 1];
    const fileName = this.generateFilename(
      BREADCRUMB_NAMES[currentPath] || 'Export',
      'pdf'
    );

    // Call the PDF export function with prepared data
    this.exportCurrentDataToPDF({
      title: `${BREADCRUMB_NAMES[currentPath].replace(' ', '_')} - ${reportType}`,
      watermarkText: 'Dentalcorp',
      colorRules: [],
      forceSingleTable: true,
      pageSize: 'A3',
      pageOrientation: 'landscape',
      baseFontSize: this.visibleColumns !== null && this.visibleColumns.size > 10 ? 8 : 10,
      minFontSize: this.visibleColumns !== null && this.visibleColumns.size > 10 ? 6 : 8,
      targetPadding: 2.5,
      lineHeight: 0.9,
      fieldsToTrim: new Set(['parentKey', 'oldValue', 'newValue']),
      data: data,
      exportFileName: fileName
    });
  }

  // Export current data to PDF
  exportCurrentDataToPDF(options?: ExportPdfOptions): void {
    const data: any[] = options.data;
    const colConfigs: ColumnConfig[] = this.gridConfig.columns
      .filter(col => this.visibleColumns.has(col.jsonKey))
      .map(c => ({ ...c }));

    // Build pdf columns from ColumnConfig
    const pdfColumns = colConfigs.map(c => ({
      header: c.label,
      field: c.jsonKey
    }));

    const title = options?.title ?? 'Export — Data';
    const watermarkText = options?.watermarkText ?? 'Dentalcorp';
    const pageSize = options?.pageSize ?? 'A4';
    const orientation = options?.pageOrientation ?? 'landscape';
    const baseFontSize = options?.baseFontSize ?? 10;
    const minFontSize = options?.minFontSize ?? 8;
    const targetPadding = options?.targetPadding ?? 0; // << minimal gaps
    const lineHeight = options?.lineHeight ?? 0.9;
    const forceSingleTable = options?.forceSingleTable ?? true;

    // Prepare headers/fields arrays
    const headers = pdfColumns.map(c => c.header.toUpperCase());
    const fields = pdfColumns.map(c => c.field);

    // Fit computation to keep all columns together
    const margins: [number, number, number, number] = [40, 60, 40, 40];
    const { fontSize: fittedFontSize, pageSize: fittedPageSize } =
      forceSingleTable
        ? computeFitSettings(headers, fields, data, pageSize, orientation, margins, baseFontSize, minFontSize, targetPadding)
        : { fontSize: baseFontSize, pageSize };

    // Coloring rules map
    const rules = options?.colorRules ?? [];
    const rulesMatch = (row: any, field: string) => {
      for (const r of rules) {
        const colOk = !r.columns || r.columns.includes(field);
        const rowOk = !r.rowWhen || r.rowWhen(row);
        const cellOk = !r.cellWhen || r.cellWhen(row, field);
        if (colOk && (rowOk || cellOk)) return r;
      }
      return undefined;
    };

    // Header row with minimal padding
    const headerRow = headers.map(text => ({
      text,
      bold: true,
      color: '#0D47A1',
      fillColor: '#E3F2FD',
      margin: [0, targetPadding, 0, targetPadding],
      noWrap: true
    }));

    // Build body rows: formatting -> sanitize -> truncate -> apply coloring rule -> minimal padding
    const body = [
      headerRow,
      ...data.map(row =>
        fields.map(field => {
          const cfg = colConfigs.find(c => c.jsonKey === field)!;

          // Your existing type-aware formatter
          let txt = formatCellByType(cfg, row[field], 'en-IN');

          // Keep your current sanitization rules (newline strip + truncate 40)
          txt = txt.replace(/\r?\n|\r/g, ' ').replace(/\s+/g, ' ').trim();
          if (options.fieldsToTrim.has(field))
            if (txt.length > 40) txt = txt.slice(0, 40);

          // Coloring rule
          const rule = rulesMatch(row, field);
          const cell: any = {
            text: txt,
            alignment: cfg.dataType === 'number' ? 'right' : 'left',
            margin: [0, targetPadding, 0, targetPadding],
            lineHeight        // tighter rows
          };
          if (rule?.textColor) cell.color = rule.textColor;
          if (rule?.fillColor) cell.fillColor = rule.fillColor;
          if (rule?.bold) cell.bold = true;
          return cell;
        })
      )
    ];

    // All columns together: single widths array (auto)
    const widths = fields.map(() => 'auto');

    // Doc definition
    const now = new Date();
    const generatedAt = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

    const docDefinition: any = {
      info: { title, subject: `${title} (${generatedAt})` },
      pageSize: fittedPageSize,            // may be upgraded to A3
      pageOrientation: orientation,        // usually landscape
      pageMargins: margins,
      defaultStyle: { fontSize: fittedFontSize, lineHeight },   // applied globally

      watermark: { text: watermarkText, color: '#777', opacity: 0.12, angle: 35, bold: true },

      header: (currentPage: number, pageCount: number) => ({
        columns: [
          { text: title, style: 'headerLeft', color: '#1976D2' },
          { text: `Page ${currentPage} of ${pageCount}`, alignment: 'right', style: 'headerRight', color: '#555' }
        ],
        margin: [40, 20, 40, 0]
      }),

      footer: () => ({
        columns: [
          { text: `Generated: ${generatedAt}`, alignment: 'left', color: '#444' },
          { text: new Date().toLocaleTimeString(), alignment: 'right', color: '#444' }
        ],
        margin: [40, 10, 40, 0],
        fontSize: 9
      }),

      content: [
        { text: title, style: 'title', color: '#0D47A1', margin: [0, 0, 0, 8] },
        {
          table: { headerRows: 1, widths, body },
          // Tight layout: zero paddings and thin borders
          layout: {
            paddingLeft: () => targetPadding,
            paddingRight: () => targetPadding,
            paddingTop: () => targetPadding,
            paddingBottom: () => targetPadding,
            hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? 0.8 : 0.4,
            vLineWidth: () => 0.4,
            hLineColor: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? '#B0BEC5' : '#CFD8DC',
            vLineColor: () => '#CFD8DC'
          }
        }
      ],

      styles: {
        title: { fontSize: Math.max(12, fittedFontSize + 2), bold: true },
        headerLeft: { fontSize: 9 },
        headerRight: { fontSize: 9 }
      }
    };

    pdfMake.createPdf(docDefinition).download(`${options.exportFileName}`);
  }

  isErrorLog(element) {
    // Use the provided isErrorLog function if available
    if (typeof this.customIsErrorLog === 'function') {
      return this.customIsErrorLog(element);
    }

    // Otherwise use the default implementation
    if (element.pmsStatusCode) {
      return ACKNOWLEDGEABLE_ERROR_STATUS_CODES.includes(element.pmsStatusCode)
        ? true
        : false;
    }
    return false;
  }

  addAllOption() {
    if (!(this.gridConfig?.showAllPagination === false)) {
      const lastOption = document.querySelector(
        '.mat-mdc-select-panel-above .mat-mdc-option:last-child > span'
      );
      lastOption ? (lastOption.innerHTML = 'All') : null;
    }
  }

  getPageSize() {
    this.gridConfig.showAllPagination == false
      ? this.pageSizeOptions.pop()
      : null;
    return this.pageSizeOptions;
  }

  getSelectedOptionName(filterConfig: FilterConfig): string {
    const selectedOption = filterConfig.options?.data.find(
      (option: any) => option.id === this.filters[filterConfig.jsonKey]
    );
    return selectedOption ? selectedOption.name : '';
  }

  isAllOptionsSelected(filterConfig: FilterConfig): boolean {
    const selectedIds = new Set(this.filters[filterConfig.jsonKey] || []);
    const allOptionIds = (filterConfig.options?.data || [])
      .filter(option => option.id !== 'All') // exclude the 'All' option
      .map(option => option.id);

    // All options (excluding 'All') must be selected
    const allSelected = allOptionIds.every(id => selectedIds.has(id));
    return allSelected;
  }

  applyFilter(filterValue: string) {
    const searchQuery = filterValue.trim().toLowerCase();
    this.searchSubject.next(searchQuery);
  }

  applyFilters(filterConfig): void {
    localStorage.setItem('choosenFilters', JSON.stringify(this.filters));

    this.appliedFilter = true;
    if (this.filters[filterConfig.jsonKey] === 'custom') {
      const fromDate = new Date(this.customDateRange.from);
      const toDate = new Date(this.customDateRange.to);

      this.filters[filterConfig.jsonKey] = {
        startDate: fromDate.toISOString(),
        endDate: toDate.toISOString(),
      };
    } else if (typeof this.filters[filterConfig.jsonKey] === 'object') {
      this.customDateRange.from = this.filters[filterConfig.jsonKey].startDate;
      this.customDateRange.to = this.filters[filterConfig.jsonKey].endDate;
    }
    this.pageNumber = 1;
    this.fetchData();
    this.closeFilterDropdowns();
    this.getDateFilterLabel();
  }

  clearFilters(filterConfig: FilterConfig): void {
    this.filters[filterConfig.jsonKey] = [];
    this.closeFilterDropdowns();
  }

  cancelFilters(filterConfig: FilterConfig): void {
    this.filters = {
      auditReportName: [
        { id: 'PMSMaster', name: 'PMSMaster' }
      ],
      sortOrder: 'desc'
    };

    this.resetAll = true;
    localStorage.removeItem('choosenFilters');
    this.appliedFilter = false;
    this.fetchData();
    this.closeFilterDropdowns();
  }

  closeFilterDropdowns(): void {
    Object.keys(this.isFilterDropdownOpen).forEach((key) => {
      this.isFilterDropdownOpen[key] = false;
    });
  }

  handleError(error: any): void {
    this.showErrorCard = true;
    this.isLoading = false;

    if (error.error && error.error.errorMessage) {
      this.errorTitle = error.error.errorMessage;
      this.errorMessage =
        error.error.detailedErrorMessage ||
        'No detailed error message provided.';
    } else if (error.status === 408) {
      this.errorTitle = 'Request Timeout';
      this.errorMessage =
        'The server is taking too long to respond. Please check your internet connection and try again.';
    } else if (error.status === 403) {
      this.errorTitle = 'Forbidden';
      this.errorMessage =
        "You don't have permission to access this information. If you believe this is a mistake, please contact support.";
    } else if (error.status === 404) {
      this.errorTitle = 'Not Found';
      this.errorMessage =
        'The requested information could not be found. It may have been moved or deleted.';
    } else if (error.status >= 500) {
      this.errorTitle = 'Internal Server Error';
      this.errorMessage =
        'There was a problem with the server. Please try again later or contact support if the problem persists.';
    } else {
      this.errorTitle = error.error?.errorMessage || 'Unexpected Error';
      this.errorMessage = error.error?.detailedErrorMessage ||
        'An unexpected error occurred. Please try again or contact support if the problem continues.';
    }

    this.changeDetectorRef.detectChanges();
  }

  onReloadAfterError() {
    this.closeErrorCard();
    this.fetchData();
  }

  closeErrorCard() {
    this.showErrorCard = false;
    this.changeDetectorRef.detectChanges();
  }

  //******************************// 
  // Column Prefrences //
  //******************************// 
  initializeVisibleColumns() {
    const hiddenColumns = this.columnPreference.HiddenColumns;
    this.gridConfig.columns.forEach(col => {
      if (!hiddenColumns.has(col.jsonKey)) {
        this.visibleColumns.add(col.jsonKey);
      }
    });
  }

  getPreferenceKeyName(): ColumnPreference {
    switch (this.uniqueTableName) {
      case 'PMSMaster':
        return {
          PreferenceKeyName: 'pms_master',
          HiddenColumns: new Set([])
        };
      case 'PracticeMaster':
        return {
          PreferenceKeyName: 'practice_master',
          HiddenColumns: new Set([])
        };
      case 'PMSObject':
        return {
          PreferenceKeyName: 'pms_object',
          HiddenColumns: new Set([])
        };
      case 'PHIMaster':
        return {
          PreferenceKeyName: 'phi_master',
          HiddenColumns: new Set([])
        };
      case 'LoadSchedule':
        return {
          PreferenceKeyName: 'load_schedule',
          HiddenColumns: new Set([])
        };
      case 'PracticeLoadConfiguration':
        return {
          PreferenceKeyName: 'practice_load_configuration',
          HiddenColumns: new Set([])
        };

      case 'PracticeInstallationLog':
        return {
          PreferenceKeyName: 'practice_installation_log_report',
          HiddenColumns: new Set([])
        };
      case 'PMSAgentHealthStatusReport':
        return {
          PreferenceKeyName: 'pms_agent_health_status_report',
          HiddenColumns: new Set([])
        };
      case 'PracticeErrorLog':
        return {
          PreferenceKeyName: 'practice_error_log_report',
          HiddenColumns: new Set([])
        };
      case 'PracticeLoadDetail':
        return {
          PreferenceKeyName: 'practice_load_detail_report',
          HiddenColumns: new Set([])
        };
      case 'UserLogs':
        return {
          PreferenceKeyName: 'user_logs_report',
          HiddenColumns: new Set([])
        };
      case 'AuditReport':
        return {
          PreferenceKeyName: 'audit_report',
          HiddenColumns: new Set([])
        };
      case 'LastSevenDaysLoadReport':
        return {
          PreferenceKeyName: 'last_seven_days_load_report',
          HiddenColumns: new Set([])
        };

      case 'PMSAgentRelease':
        return {
          PreferenceKeyName: 'pms_agent_releasec',
          HiddenColumns: new Set([])
        };
      case 'PMSAgentReleaseMapping':
        return {
          PreferenceKeyName: 'pms_agent_release_mapping',
          HiddenColumns: new Set([])
        };

      case 'UserRoleManagement':
        return {
          PreferenceKeyName: 'user_role_management',
          HiddenColumns: new Set([])
        };

      default:
        return {
          PreferenceKeyName: '',
          HiddenColumns: new Set([])
        };
    }
  }

  toggleColumnSelector(event: MouseEvent) {
    event.stopPropagation();
    this.isColumnSelectorOpen = !this.isColumnSelectorOpen;
  }

  toggleColumnVisibility(jsonKey: string) {
    if (this.visibleColumns.has(jsonKey)) {
      this.visibleColumns.delete(jsonKey);
    } else {
      this.visibleColumns.add(jsonKey);
    }
  }

  isColumnVisible(jsonKey: string): boolean {
    return this.visibleColumns.has(jsonKey);
  }

  onSavePreferences(event: MouseEvent) {
    event.stopPropagation();
    this.saveColumnPreferences();
  }

  async loadColumnPreferences(preferenceKeyName: string): Promise<void> {
    if (!this.userId) {
      this.userId = localStorage.getItem('externalUserId');
      if (!this.userId) {
        const userClaims = await this.oktaAuth.getUser();
        this.userId = userClaims?.sub || null;
        localStorage.setItem('externalUserId', this.userId || '');
      }
    }

    const subscription = (await this.columnPrefService.getColumnPreferences(preferenceKeyName))
      .pipe(takeUntil(this.destroy$))
      .subscribe(prefs => {
        if (prefs !== null) {
          const preferences = JSON.parse(prefs.preferenceValue);
          this.visibleColumns = new Set(preferences.columns);
        } else {
          const cols = this.gridConfig.editable || this.gridConfig.deletable
            ? ['action', ...this.gridConfig.columns.map((c) => c.jsonKey)]
            : this.gridConfig.columns.map((c) => c.jsonKey);
          this.visibleColumns = new Set(cols);
        }
      });

    this.subscriptions.push(subscription);
  }

  saveColumnPreferences(): void {
    this.isSavingPreferences = true;

    if (!this.userId) {
      this.userId = localStorage.getItem('externalUserId');
    }

    const body = {
      externalUserId: this.userId,
      preferenceKey: this.columnPreference.PreferenceKeyName,
      preferenceValue: JSON.stringify({ columns: Array.from(this.visibleColumns) })
    };

    const subscription = this.columnPrefService
      .saveColumnPreferences(this.columnPreference.PreferenceKeyName, body)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSavingPreferences = false;
          this.changeDetectorRef.markForCheck(); // Important for OnPush
        })
      )
      .subscribe({
        next: (ok) => {
          // Ensure UI updates are within Angular zone
          this.changeDetectorRef.markForCheck();
          this.isSavingPreferences = false;
          this.toastComponent?.showToast({
            severity: 'success',
            title: 'Success',
            message: 'Column preferences saved successfully',
            duration: 3000
          });
        },
        error: (err) => {
          console.error('Failed to save column preferences', err);
          // Optionally show error toast
          this.toastComponent?.showToast({
            severity: 'error',
            title: 'Error',
            message: 'Failed to save column preferences',
            duration: 3000
          });
          this.changeDetectorRef.markForCheck();
        }
      });

    this.subscriptions.push(subscription);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up any remaining subscriptions
    this.subscriptions.forEach(sub => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });
    this.subscriptions = [];
  }
}
